/** Shared data shapes: players (units) and team palettes. Part of src/types.ts (re-exported there; import from '../types'). */
import type { SecondaryKind } from './loadout';
import type { MissileLock } from './flight';
import type { ArmourWorn } from './armour';
import type { BotCommand, BotConfig } from './bots';
import type { BodyKind } from './body';
import type { Vec3 } from './math';
import type { Dig } from './ground';
import type { ChargeFuse } from './charge';
import type { AirborneCause } from './state';
import type { PlayerLoadout, WeaponKey } from './weapons';
import type { SeatState } from './body';
import type { BuildKind } from './inputs';

// =============================================================================
// PLAYER
// =============================================================================

/**
 * A person's stance (decision 65, BUILDLIST 62.2): standing (the default), crouched (kneeling when
 * still, a low walk when moving) or prone (lying, a crawl). The posed model's sizes per stance are
 * constants STANCE_SIZE; the sim half (eye height, hitbox, cover, perception, the bot) reads those.
 */
export type Stance = 'stand' | 'crouch' | 'prone';
/** 63.8: the pace on foot — the shooting walk (today's), a move (faster, its fire unaimed), a sprint (a fast jog, no fire). */
export type Gait = 'walk' | 'move' | 'sprint';
/**
 * 66.7 (a): how a person holds cover (lane E's poses, rendering/personModel.ts): crouched behind low cover and rising
 * over it to fire ('low'), kneeling at a rock's edge and leaning out round it ('edge'), standing at a corner or a trunk
 * and leaning out round it ('corner'), prone below a crest and sliding up over it ('crest'). Each is held down behind the
 * cover or out of it; the posed sizes, down and out, are constants COVER_POSE_SIZE.
 */
export type CoverPose = 'low' | 'edge' | 'corner' | 'crest';
/**
 * 66.7: how a unit holds the cover it is at — the pose (lane E's), the side it leans out to (1 right, −1 left, seen facing
 * the cover; edge and corner), and whether it is out of the cover now (firing) or down behind it. Set through the unit's
 * intent (UnitIntent.cover) by the bots at their posts and by the player's take-cover controls; kept by the Match while the
 * unit stands still on the ground. The drawing poses it (PersonAnim.cover). Not yet: the hit volume and the eyes following
 * the lean — the body is where it stands, its stance the pose's (low, edge: crouched; corner: standing; crest: prone).
 */
export interface CoverHold {
  pose: CoverPose;
  side?: 1 | -1;
  out: boolean;
}
/** 65.1 (decision 118): the Ground rung's weapons — the rifle (today's gun) and the pistol; S7 the rocket; 65.13 / 65.14 the SMG and the LMG; 65.31 the 40 mm break-barrel launcher; 65.49 the anti-dropship missile (`missile`). */
export type GroundWeaponKind = 'rifle' | 'pistol' | 'rocket' | 'smg' | 'lmg' | 'gl40' | ClassicGunKind | 'missile';
/**
 * 65.43 (DECISIONS 194, the owner: "I don't want the classic guns to be reskins of existing guns"): the classic guns, each a
 * weapon of its own on its own cartridge — options in loadouts, never a default. Their keys are stable (lane E's models read
 * them): the M1 Garand, the Springfield, the M1911 (a sidearm), the Thompson, the BAR, a Panzerfaust-style tube.
 */
export type ClassicGunKind = 'garand' | 'springfield' | 'm1911' | 'thompson' | 'bar' | 'panzerfaust';
/** 65.30 (decision 179): the round a bullet weapon fires (constants/ammo.ts CARTRIDGES) — 5.56 for a later rifle; 65.43 the classic guns' .30-06 and .45 ACP. */
export type CartridgeKind = '9mm' | '7.62' | '.50' | '5.56' | '.30-06' | '.45';
/** 65.13 / 65.14: the long gun a person carries as their primary (absent = the rifle) — a kit choice, kept across respawns; 65.43 the classic long guns. */
export type LongGunKind = 'rifle' | 'smg' | 'lmg' | 'garand' | 'springfield' | 'thompson' | 'bar';
export const LONG_GUNS: readonly LongGunKind[] = ['rifle', 'smg', 'lmg', 'garand', 'springfield', 'thompson', 'bar'];
/** 65.43: the sidearm (absent = the pistol): the M1911 in the pistol's place. */
export type SidearmKind = 'pistol' | 'm1911';
/** 201 (8): a specialist role (absent = the line rifleman) — drawn (rendering/roleLooks.ts); 69.45: the support carries an ammunition case (GameConfig.supportRole, core/supportRole.ts), the medic and the engineer drawn only; 69.46: the crewman works a vehicle's seat faster (GameConfig.crewmanRole, core/crewman.ts). */
export type UnitRole = 'medic' | 'engineer' | 'support' | 'crewman';

export interface PlayerState {
  id: number;
  name: string;
  companyName: string;
  teamIndex: number;         // index into TEAMS array for colors
  planetIndex: number;
  surfaceDir: Vec3;          // normalized direction from planet center
  health: number;
  alive: boolean;
  /** X22: got out through its team's exit — off the field (alive false) but a survivor to the sector (core/withdrawal.ts). */
  extracted?: boolean;
  isBot: boolean;
  colorIndex: number;        // legacy, use teamIndex for colors
  cbp: number;               // Contract Budget Points
  selectedWeapon: WeaponKey;
  loadout: PlayerLoadout;    // ammo counts and weapon upgrades
  // Movement
  movementRange: number;     // max movement per turn (upgradeable)
  movementRemaining: number; // movement left this turn
  // Airborne state (when knocked off planet)
  airborne: boolean;
  /** 69.3c: an arrival still coming down in its pod (seconds left; ARRIVAL.fallSeconds at a respawn, at a person scale) — it can't move or fire, isn't seen, can't be hit and doesn't count on a point until it lands. */
  landingLeft?: number;
  /** 69.3b's follow-up: a dropship's rider walking out of the troop bay — the waypoints left (surface directions: the aisle, the ramp's foot, its exit). Scripted (core/dropship.ts stepRampWalks): its intent idle till it is out. */
  rampWalk?: Vec3[];
  /** DECISIONS 208: a wave's arrival back alive in its side's wave ship (seated in the container, parked or in the air) — still, not seen, not hit, not on a point, till its ramp walk (core/dropship.ts boardShips). */
  boarded?: true;
  /** 69.17 step 3: in the pilot's seat of this flight (boarded too: aboard, out of the world) */
  piloting?: number;
  airbornePos: Vec3 | null;  // world position when airborne
  /**
   * 63.1d step 2: an exposed hatch occupant's feet in the world (a transport's cupola gunner, its body above the rim), set
   * each tick its vehicle stands (core/drive.ts); the body, the eye and the muzzle stand there in place of the ground.
   * Absent otherwise — on foot, and in a closed seat (the driver's, the bays: inside the hull).
   */
  seatPos?: Vec3;
  airborneVel: Vec3 | null;  // velocity when airborne
  /** 63.19 (GameConfig.vehicleImpact): the last hull that struck this person and the action tick it did — a pair is struck at most once a VEHICLE_IMPACT.pairSeconds. Absent: never struck (or the switch off). */
  struck?: { hull: number; tick: number };
  /** Why the unit is in the air: a deliberate jump, a blast knockback, or a hazard. Drives the per-cause fall damage. */
  airborneCause?: AirborneCause | null;
  /** 63.15 (GameConfig.jumpFeel): a jump's take-off height — its distance from its planet's centre — under which the felt gravity returns. Absent: none (no jump under way, or the switch off). */
  jumpFrom?: number;
  /** Seconds spent airborne so far; past MAX_AIRBORNE_TIME the unit is lost in space (no stable orbits). */
  airborneTime?: number;
  /** Phase 57: which way the unit faces (unit tangent, world space) — set by walking and firing; undefined = omnidirectional. */
  facing?: Vec3;
  /** R12: what this unit's body is (core/body.ts). Absent = the clock's reading: a person on the action clock, the classic vehicle on the turn clock. */
  body?: BodyKind;
  /** R12 step 3: the body and seat this unit is parked in (core/seats.ts). Absent = on its own feet (every unit at setup). */
  mount?: { bodyId: number; seat: number };
  /** WORKBOARD I2: drones this unit carries (consumable). Absent = none. */
  drones?: number;
  /** 65.15: the grenades it carries (absent = none). */
  grenades?: { he: number; smoke: number };
  /** 65.23 (with GameConfig.grenadeStock): what a unit is issued — a respawn or a crate tops `grenades` back up to it, from the side's stock. */
  grenadeIssue?: { he: number; smoke: number };
  /** 65.15: the tick it may throw again (a throw takes GRENADES.throwSeconds). */
  throwReady?: number;
  /** 216 (GameConfig.realTiming): a throw winding up — the grenade spent, leaving the hand along `dir` at tick `at` (REAL_TIMING.throwWindSeconds); a death, a seat or the air first gives it back. Absent: none (or the switch off). */
  throwWind?: { kind: 'he' | 'smoke'; dir: Vec3; at: number };
  /** 216 (GameConfig.realTiming): a stance change under way — the eye eases from `fromEye` (its scale when the change began) to the new stance's over `total` seconds, `left` to go; the hit shape is `from`'s for the first half (core/stance.ts). Absent: none (or the switch off). */
  stanceShift?: { from: Stance; fromEye: number; left: number; total: number };
  /** 65.19: explosive payloads it carries for its drones (the scanner is free). Absent = none. */
  charges?: number;
  /** 65.19: what it was issued — a crate tops drones and charges back up to these. Absent = payloads off. */
  payloadIssue?: { drones: number; charges: number };
  /** 65.16: the drones it carries are strike drones (a kit choice: one HE charge instead of the scout's eyes). Absent = scout drones. */
  droneKind?: 'strike';
  /** I3: what paid for this unit's last probe shot ('drone' = a carried drone, the cheapest drone's cost), for a refund. */
  probePaidBy?: 'drone';
  /**
   * I2, ruling 25 as clarified (owner): a drone's operator is busy only while tasking it (setting its
   * course) and launching it — it stands still then; once the drone is away it flies its route on its own.
   */
  droneTasking?: boolean;
  /** I2: seconds of the launch still to go (the operator stands still until it is 0). */
  droneLaunchLeft?: number;
  /** R12 step 3: the unit's own seats, when it is a vehicle with crew records in them (ruling 19); absent = the implicit crew of one gunner. Nothing reads it yet. */
  seats?: SeatState[];
  /** Action clock (Phase 59): the way the unit looks along the ground — a unit tangent vector, carried as it walks. */
  heading?: Vec3;
  /** Action clock: aim pitch above the ground plane, radians (−π/2 … π/2). */
  aimPitch?: number;
  /** 65.25a cut 2: the aim across, radians to the right of the heading (the player's aim assist: lead and wind), clamped to AIM_YAW_MAX; set only from an intent that carries it (absent: along the heading — every bot). */
  aimYaw?: number;
  /** Decision 65: crouched or prone (absent = standing). Set by the sim half of 62.2; the person view poses it. */
  stance?: Stance;
  /** 66.7: the cover it holds (absent = none): set by its intent, kept while it stands still on the ground (CoverHold). */
  cover?: CoverHold;
  /** 63.8: the gait of its last step when faster than the walk (absent: walking, or still) — the person view poses it, the HUD shows it. */
  gait?: 'move' | 'sprint';
  /** 66.8 (§5.18): digging in — its dig as dug so far (on the planet from its first quarter), how far along (0 … 1), its health when it began (a hit stops it). Lane E's pose reads it. */
  digAt?: { dig: Dig; progress: number; health: number; /** deepening the dig into a foxhole (`progress` is then the foxhole's; the planet keeps the scrape until a quarter passes where it covers what the scrape did) */ deepen?: true };
  /** Decision 134: its last step was a scramble — up ground too steep to walk, slowly (absent = walking). The person view may pose it; a bot reads it as progress, not stuck. */
  scrambling?: boolean;
  /** 63.5 step 4 (with ground materials): on ice, the walk's carried velocity (a tangent vector, u/s) — it closes on the way asked at the ice's grip and drifts down a slope; absent off ice. */
  slip?: Vec3;
  /** 65.3: the weapon in hand (absent = the primary — the rifle unless the kit says otherwise); 65.2: seconds left of a reload, and its full length; 65.3: seconds left of a switch. The person view poses them. */
  weapon?: GroundWeaponKind;
  /** 65.13 / 65.14: the primary long gun (absent = the rifle). */
  primary?: LongGunKind;
  /** 65.32: the unit's secondary from its squad slot — the rocket tube (65.29) its own (absent: none; or, with no layout on its side, the side's switch as before). */
  secondary?: SecondaryKind;
  /** 65.32: the player's own loadout, on the human's starting unit only (MatchSetup.playerLoadout) — the weapons the human's X steps through, outside the slots (absent: the slots). */
  carry?: GroundWeaponKind[];
  /** 65.43: the sidearm, in the pistol's place (absent = the pistol). */
  sidearm?: 'm1911';
  /** 201 (8): its role from its squad slot (MemberLoadout.role; absent = the line). Drawn; 69.45: the support's case (below) with GameConfig.supportRole. */
  role?: UnitRole;
  /** 69.45 (GameConfig.supportRole): the support's ammunition case — magazines in it (absent = full, SUPPORT.case: a life's, as the bandages); and, while it is set down, the case's structure id (the support carries none meanwhile). */
  ammoCase?: number;
  caseDown?: number;
  /** 69.47 step 1 (GameConfig.charges; core/charges.ts): the engineer's placed charges carried (absent = full, CHARGE.carried: a life's; `charges` is the drone's payloads, 65.19), and a placing under way — the kneel's seconds left and the fuse it sets. */
  demoCharges?: number;
  chargeLeft?: number;
  chargeFuse?: ChargeFuse;
  /** 65.43: the trigger held at the last tick — set only while a semi-automatic or bolt gun is in hand (it fires on the press). */
  triggerHeld?: true;
  /** 185: the armour it wears — a helmet, a vest — each worn down by what it stops (absent: none). */
  armour?: ArmourWorn;
  /** 65.31 step 2: the 40 mm's rounds a life this unit is issued — its slot's count, else GameConfig.gl40Rounds (absent: the launcher's own, one in the breech and its spares). */
  gl40Issue?: number;
  /** 65.2: rounds left in each weapon's magazine (absent = full). */
  magazines?: Partial<Record<GroundWeaponKind, number>>;
  /** S7: spare rounds carried for a weapon with a limited supply (the rocket tube; absent = its full supply). */
  reserves?: Partial<Record<GroundWeaponKind, number>>;
  /** Decision 128: shells carried from a crate to the team's gun, loaded when the unit gets in (absent = none). */
  shellLoad?: number;
  reloadLeft?: number;
  reloadTime?: number;
  switchLeft?: number;
  /** 65.7: bandages carried (absent = BANDAGE.carried, the full issue); a bandage being applied: seconds left and its whole time (core/medical.ts). */
  bandages?: number;
  healLeft?: number;
  healTime?: number;
  /** 65.11: seconds left of an interrupted bandage of one's own — the next one resumes from there (absent = none). */
  healSaved?: number;
  /** 125a: beacons carried (absent = none; a squad's first unit is issued BEACON.carried), and a planting under way: seconds left (core/beacon.ts). */
  beacons?: number;
  plantLeft?: number;
  /** 69.2b: what the planting under way puts up (core/build.ts) — absent: the beacon (125a's planting, unchanged). */
  planting?: BuildKind;
  /** 65.8: the bandage being applied is a teammate's (the medic's id); and, on the medic, the teammate being bandaged. */
  healBy?: number;
  treating?: number;
  /** Action clock: seconds until the unit can fire again. */
  fireCooldown?: number;
  /** 65.49 step 2: the anti-dropship missile's seeker while the weapon is up (core/homing.ts stepLock); absent otherwise */
  missileLock?: MissileLock;
  /** Capture-under-fire rule (modeConfig.captureSuppressSeconds): seconds left during which this unit, hit, cannot capture (it still contests). */
  suppressed?: number;
  /** 65.28, the grip `beat`: seconds left during which near rounds pass this unit by (the beat it ducked, then its burst). */
  suppressRest?: number;
  /** 65.28, the grip `weight`: the near rounds it carries (leaking at SUPPRESSION.weight's rate); at its `rounds`, a mark. */
  suppressLoad?: number;
  // Wormhole tracking (airborne only)
  lastWormholeId: number | null;  // prevents re-entry at exit wormhole
  wormholeTransits: number;       // death counter (airborne only)
  // Persisted aim state
  lastAimAzimuth: number;    // last aim azimuth (radians)
  lastAimElevation: number;  // last aim elevation (radians)
  lastAimPower: number;      // last aim power (0-100)
  // Stats
  shotsFired: number;
  /** Probes this unit has launched (queued ones included, refunded ones not). Bots read the team total (scouting). */
  probesFired?: number;
  damageDealt: number;
  kills: number;
  // Environmental kill attribution — tracks who last damaged/knocked this player
  lastDamagedBy?: number;
  // Bot AI
  botConfig?: BotConfig;   // undefined for human players
  unitIndex: number;       // which unit within the team (0, 1, 2)
  flagsRemaining: number;  // flags available to plant
  jumppackFuel: number;    // jumppack fuel units available
  jumpBonus?: number;      // campaign jump upgrade velocity bonus
  // Bot combat memory — reuse aim params when re-engaging same position
  botLastFireParams?: {
    azimuth: number; elevation: number; power: number; weaponKey: WeaponKey;
    fromPlanetIndex?: number;   // where the bot stood when it fired — re-engagement only valid from the same spot
    fromSurfaceDir?: Vec3;
  };
  botLastFireTargetPos?: Vec3;
  /** Round flows: the shot the team's joint plan assigned this unit (ai/bot/teamPlan.ts), used when its turn comes if it still stands where it planned. */
  botPlannedShot?: { round: number; fromPlanetIndex: number; fromSurfaceDir: Vec3; fire: { azimuth: number; elevation: number; power: number; weaponKey: WeaponKey; targetPos?: Vec3 } };
  // Bot command — order from human team leader
  pendingCommand?: BotCommand;
  holdFire?: boolean;          // when true, bot will not fire (weapons hold)
  // Status effects
  empTurnsRemaining?: number;  // turns of EMP disable (no trajectory preview)
  tetheredTurns?: number;      // turns of root (cannot move/jump)
  /** The unit's kit (launcher / suit / equipment / traits) — core/unitModifiers.ts. */
  kit?: UnitKit;
  /** Resolved from the kit and the match config at match start — core/unitModifiers.ts. */
  modifiers?: UnitModifiers;
}

/** Everything that tunes one unit's numbers, resolved from its kit at match start; see core/unitModifiers.ts. */
export interface UnitModifiers {
  /** 0–1: how much of the true flight the aim preview shows and how finely. 1 = the whole flight. */
  previewQuality: number;
  probeRangeMult: number;
  healMult: number;
  /** Flat damage removed from every blast or beam that hurts this unit. */
  armorReduction: number;
  /** × projectile fuel duration (range). */
  fuelMult: number;
  /** × this unit's weapon damage and blast radius. */
  damageMult: number;
  blastMult: number;
  /** Movement points per turn. */
  movementRange: number;
  /** Added to jump launch velocity. */
  jumpVelocityBonus: number;
  /** × landing damage when the unit's own jump put it in the air. */
  jumpFallDamageMult: number;
  /** Phase 57.9: × the distance at which a guard perceives THIS unit (camo: < 1 = harder to see). */
  stealthMult: number;
  /** Phase 57.9: × THIS unit's perception range when it is a guard (sensors: > 1 = sees farther). */
  sensorMult: number;
  /** 65.14: × the walk speed on the action clock (the LMG's carry penalty); absent = 1. */
  walkSpeedMult?: number;
}

// ---- Unit kit: the three sources the modifiers are resolved from ----

/** The unit's own growth. Earned, not bought (today: the campaign's fitness / jump levels, 0–3). */
export interface UnitTraits {
  fitness: number;
  jump: number;
}

/** Launcher mods (the weapon mount), tiered 0–3. */
export type LauncherModKey = 'preview' | 'range' | 'yield' | 'blast' | 'probe';
/** Suit mods, tiered 0–3. armor / medkit are in the store today; the rest exist in the model only. */
export type SuitModKey = 'armor' | 'medkit' | 'padding' | 'thrusters' | 'servos' | 'camo' | 'sensors';
/** Slotted equipment (EQUIPMENT_SLOTS of them). Catalogue in core/unitModifiers.ts; nothing acquires these yet. */
export type EquipmentKey = 'shockAbsorbers' | 'ballastHarness' | 'ghillieCloak' | 'sensorPack';

export interface EquipmentDef {
  key: EquipmentKey;
  name: string;
  description: string;
  /** Multiplicative for rates (…Mult), additive for amounts (armor, movement, jump velocity). */
  modifiers: Partial<UnitModifiers>;
}

export interface UnitKit {
  launcher: Partial<Record<LauncherModKey, number>>;
  suit: Partial<Record<SuitModKey, number>>;
  equipment: EquipmentKey[];
  traits: UnitTraits;
}

export interface PlayerColor {
  hex: string;
  r: number;
  g: number;
  b: number;
}

// =============================================================================
// TEAM PALETTES
// =============================================================================

/** Helmet hardware variant on the player model (rendering/playerModel.ts). */
export type HelmetStyle = 'dome' | 'crest' | 'pods' | 'lampbar' | 'flaps' | 'hardhat' | 'headset' | 'cap';
/** Visor shape on the player model. */
export type VisorStyle = 'band' | 'bubble' | 'goggles' | 'slit';

export interface TeamPalette {
  name: string;
  tagline: string;
  primary: string;   // suit / body
  accent: string;    // helmet / boots
  visor: string;     // visor highlight
  detail: string;    // backpack / trim / tool
  helmet?: HelmetStyle;      // per-team helmet hardware (default dome)
  visorStyle?: VisorStyle;   // per-team visor shape (default band)
}
